// Debouncing, in the two shapes the app actually needs: republish a value once
// it settles (search typeaheads), or defer an action until the user stops
// (saving a typed field, writing a user preference).
//
// Prefer these over a local setTimeout + ref. The hand-rolled version keeps
// getting the same three things wrong: the timer isn't cleared on unmount, the
// queued call fires a closure captured several renders ago, and the handler's
// identity changes every render so listing it as an effect dependency restarts
// the timer mid-typing.
import { useEffect, useMemo, useRef, useState } from "react";

/** Identifies one independent timer within a single useDebouncedCallback. */
export type DebounceKey = string | number;

/**
 * `value`, republished only once it has stopped changing for `delay` ms.
 *
 * The current value is returned immediately on the first render — the delay
 * applies to *changes*, so a consumer never renders a blank first frame.
 * Changes are detected by identity, so pass a primitive, a memoized object, or
 * an object rebuilt on every edit; an object mutated in place is not seen as a
 * change.
 */
export function useDebouncedValue<T>(value: T, delay: number): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return settled;
}

export interface DebouncedCallback<A extends unknown[]> {
  /** Queue a call, replacing whatever was queued for the same key. */
  run: (...args: A) => void;
  /** Run the queued call now — blur, Enter, submit. No-op if nothing is queued. */
  flush: (key?: DebounceKey) => void;
  /** Drop the queued call without running it. */
  cancel: (key?: DebounceKey) => void;
  isPending: (key?: DebounceKey) => boolean;
}

interface UseDebouncedCallbackOptions<A extends unknown[]> {
  /**
   * Derives a timer key from the call's arguments, giving each key its own
   * independent debounce. Needed whenever one hook instance defers a per-item
   * action — a save per grid row, say — so row 7's keystroke can't cancel
   * row 3's pending save. Omit for a single shared timer.
   */
  key?: (...args: A) => DebounceKey;
}

/**
 * `fn`, deferred until `delay` ms after the last call. Trailing edge only.
 *
 * The returned object is stable for the component's lifetime, so it is safe in
 * a dependency array; `fn` is read at fire time, so a queued call uses the
 * newest closure rather than the one captured when it was queued; and every
 * pending timer is cleared on unmount.
 */
export function useDebouncedCallback<A extends unknown[]>(
  fn: (...args: A) => void,
  delay: number,
  options: UseDebouncedCallbackOptions<A> = {}
): DebouncedCallback<A> {
  // All three are read at fire time rather than captured, which is what lets
  // the returned object stay stable across renders.
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const keyRef = useRef(options.key);
  keyRef.current = options.key;
  const delayRef = useRef(delay);
  delayRef.current = delay;

  const pendingRef = useRef(
    new Map<DebounceKey, { timer: ReturnType<typeof setTimeout>; args: A }>()
  );

  // A queued call outlives the render that queued it, so it must not survive
  // unmount: `fn` typically sets state or fires a request.
  useEffect(() => {
    const pending = pendingRef.current;
    return () => {
      pending.forEach(({ timer }) => clearTimeout(timer));
      pending.clear();
    };
  }, []);

  return useMemo<DebouncedCallback<A>>(() => {
    const SHARED: DebounceKey = "__shared__";
    // `??`, not `||` — 0 is a legitimate key (a row id, an index).
    const resolve = (key?: DebounceKey) => key ?? SHARED;

    // Removes the queued call for `key` and hands it back, so run/flush/cancel
    // all clear the timer through one path.
    const take = (key: DebounceKey) => {
      const entry = pendingRef.current.get(key);
      if (entry) {
        clearTimeout(entry.timer);
        pendingRef.current.delete(key);
      }
      return entry;
    };

    return {
      run: (...args: A) => {
        const key = keyRef.current ? keyRef.current(...args) : SHARED;
        take(key);
        const timer = setTimeout(() => {
          pendingRef.current.delete(key);
          fnRef.current(...args);
        }, delayRef.current);
        pendingRef.current.set(key, { timer, args });
      },
      flush: (key) => {
        const entry = take(resolve(key));
        if (entry) fnRef.current(...entry.args);
      },
      cancel: (key) => {
        take(resolve(key));
      },
      isPending: (key) => pendingRef.current.has(resolve(key)),
    };
  }, []);
}
